import React, {useEffect, useCallback} from 'react'
import PropTypes from 'prop-types'
import {useDispatch, useSelector} from 'react-redux'
import {useIntl} from 'react-intl'
import {
  getConstructionSiteAnalysis,
  setSelectedConstructionSiteId,
  resetWorkStatus,
  selectWorkStatusLoading,
  selectWorkStatusError,
  selectWorkStatusAnalysis,
  selectSelectedConstructionSiteId,
} from '../../redux/slices/workstatus.slice'
import {
  constructionSiteLookup,
  selectConstructionSiteLookupList,
} from '../../redux/slices/lookup.slice'
import {Loading} from '../Loading'
import {WorkStatusSummaryCards} from './WorkStatusSummaryCards'
import {WorkStatusChart} from './WorkStatusChart'
import {WorkStatusTaskTable} from './WorkStatusTaskTable'

const WorkStatus = ({projectId}) => {
  const intl = useIntl()
  const dispatch = useDispatch()

  const loading = useSelector(selectWorkStatusLoading)
  const error = useSelector(selectWorkStatusError)
  const analysis = useSelector(selectWorkStatusAnalysis)
  const selectedId = useSelector(selectSelectedConstructionSiteId)
  const constructionSites = useSelector(selectConstructionSiteLookupList)

  useEffect(() => {
    dispatch(constructionSiteLookup({projectId}))
    return () => {
      dispatch(resetWorkStatus())
    }
  }, [dispatch, projectId])

  const handleSiteChange = useCallback(
    (e) => {
      const id = parseInt(e.target.value, 10)
      if (!id) {
        dispatch(setSelectedConstructionSiteId(null))
        return
      }
      dispatch(setSelectedConstructionSiteId(id))
      dispatch(getConstructionSiteAnalysis(id))
    },
    [dispatch]
  )

  return (
    <div className='py-5'>
      <div className='card mb-5'>
        <div className='card-body py-5'>
          <div className='row align-items-center'>
            <div className='col-md-4'>
              <label className='fw-bold text-gray-700 fs-6 mb-2'>
                {intl.formatMessage({id: 'WORKSTATUS.SELECT.SITE'})}
              </label>
              <select
                className='form-select form-select-solid'
                value={selectedId ?? ''}
                onChange={handleSiteChange}
              >
                <option value=''>
                  {intl.formatMessage({id: 'WORKSTATUS.SELECT.PLACEHOLDER'})}
                </option>
                {Array.isArray(constructionSites) &&
                  constructionSites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
              </select>
            </div>
            {analysis && (
              <div className='col-md-8'>
                <div className='text-muted fs-7 mt-5 mt-md-0'>
                  {analysis.projectName}
                  {analysis.projectStartDate && (
                    <span className='ms-4'>
                      {intl.formatDate(new Date(analysis.projectStartDate), {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                      {' → '}
                      {analysis.projectEndDate
                        ? intl.formatDate(new Date(analysis.projectEndDate), {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })
                        : '—'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {!selectedId && (
        <div className='alert alert-info d-flex align-items-center'>
          <i className='bi bi-info-circle fs-2 me-4 text-info' />
          <div className='d-flex flex-column'>
            <h4 className='mb-1 text-info'>
              {intl.formatMessage({id: 'WORKSTATUS.SELECT.INFO.TITLE'})}
            </h4>
            <span>{intl.formatMessage({id: 'WORKSTATUS.SELECT.INFO.BODY'})}</span>
          </div>
        </div>
      )}

      {loading && <Loading />}

      {error && !loading && (
        <div className='alert alert-danger'>
          <strong>Error:</strong> {error}
        </div>
      )}

      {analysis && !loading && (
        <>
          <WorkStatusSummaryCards analysis={analysis} />
          <WorkStatusChart analysis={analysis} />
          <WorkStatusTaskTable tasks={analysis.tasks ?? []} />
        </>
      )}
    </div>
  )
}

WorkStatus.propTypes = {
  projectId: PropTypes.number.isRequired,
}

export {WorkStatus}
